import React from 'react';
import PropTypes from 'prop-types';
import HomeSection from '../HomeSection';
import MediaSubLabel from '../../../molecules/MediaSubLabel';
import TrackRow from '../../../molecules/TrackRow';
import IconButton from '../../../atoms/IconButton';
import MediaUploadButton from '../../../molecules/MediaUploadButton';
import trashIcon from '../../../../images/trash.svg';
import './index.scss';

const ArrowIcon = ({ direction = 'up', ...props }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {direction === 'up' ? (
      <path d="M12 19V5M5 12l7-7 7 7" />
    ) : (
      <path d="M12 5v14M5 12l7 7 7-7" />
    )}
  </svg>
);

ArrowIcon.propTypes = {
  direction: PropTypes.oneOf(['up', 'down']),
};

function EmptyMediaPanel({ title, description, className = '' }) {
  return (
    <div className={`home-media-empty ${className}`.trim()}>
      <div className="home-media-empty__title">{title}</div>
      {description ? (
        <div className="home-media-empty__description">{description}</div>
      ) : null}
    </div>
  );
}

const buildTrackActionLabel = (baseLabel, track) => {
  const title = typeof track?.title === 'string' ? track.title.trim() : '';
  return title ? `${baseLabel}: ${title}` : baseLabel;
};

EmptyMediaPanel.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  className: PropTypes.string,
};

export default function HomeMediaSection({
  id = 'media',
  eyebrow,
  title,
  side,
  tracks = [],
  tracksLabel,
  tracksMeta,
  emptyTracksTitle,
  emptyTracksDescription,
  uploadAudioLabel = 'Upload audio',
  onUploadTrack,
  onPlayTrack,
  onSeekTrack,
  isEditing = false,
  onDeleteTrack,
  onMoveTrack,
  deletingTrackId = null,
  isReorderingTracks = false,
  deleteTrackLabel = 'Delete track',
  moveTrackUpLabel = 'Move track up',
  moveTrackDownLabel = 'Move track down',
  uploadAudioDisabled = false,
  uploadAudioTooltip = null,
  uploadAudioTooltipId,
  uploadAudioAriaLabel,
}) {
  const trackUploadButton = typeof onUploadTrack === 'function' ? (
    <MediaUploadButton
      variant="audio"
      label={uploadAudioLabel}
      onUpload={onUploadTrack}
      disabled={uploadAudioDisabled}
      tooltip={uploadAudioTooltip}
      tooltipId={uploadAudioTooltipId || `${id}-audio-upload-limit-tooltip`}
      ariaLabel={uploadAudioAriaLabel}
    />
  ) : tracksMeta;

  return (
    <HomeSection id={id} eyebrow={eyebrow} title={title} side={side} showSide={isEditing} className="home-media">
      <MediaSubLabel title={tracksLabel} meta={trackUploadButton} />
      {tracks.length > 0 ? (
        <div className="home-media__tracks">
          {tracks.map((track, i) => (
            <TrackRow
              key={track.id || i}
              index={i + 1}
              title={track.title}
              meta={track.meta}
              cover={track.cover}
              plays={track.plays}
              progress={track.progress}
              seed={track.seed ?? i + 1}
              isPlaying={track.isPlaying}
              onPlay={onPlayTrack ? () => onPlayTrack(track, { pause: track.isPlaying }) : undefined}
              onSeek={onSeekTrack ? (progress) => onSeekTrack(track, progress) : undefined}
              actions={isEditing ? (
                <div className="home-media__track-actions">
                  <IconButton
                    icon={<ArrowIcon direction="up" />}
                    label={buildTrackActionLabel(moveTrackUpLabel, track)}
                    variant="ghost"
                    size={34}
                    className="home-media__track-action"
                    onClick={() => onMoveTrack?.(track, 'up')}
                    disabled={i === 0 || isReorderingTracks || Boolean(deletingTrackId)}
                  />
                  <IconButton
                    icon={<ArrowIcon direction="down" />}
                    label={buildTrackActionLabel(moveTrackDownLabel, track)}
                    variant="ghost"
                    size={34}
                    className="home-media__track-action"
                    onClick={() => onMoveTrack?.(track, 'down')}
                    disabled={i >= tracks.length - 1 || isReorderingTracks || Boolean(deletingTrackId)}
                  />
                  <IconButton
                    icon={<img src={trashIcon} alt="" className="home-media__track-delete-icon" />}
                    label={buildTrackActionLabel(deleteTrackLabel, track)}
                    variant="ghost"
                    size={34}
                    className="home-media__track-action home-media__track-action--danger"
                    onClick={() => onDeleteTrack?.(track)}
                    disabled={isReorderingTracks || Boolean(deletingTrackId)}
                  />
                </div>
              ) : null}
            />
          ))}
        </div>
      ) : (
        <EmptyMediaPanel
          title={emptyTracksTitle}
          description={emptyTracksDescription}
          className="home-media__empty"
        />
      )}
    </HomeSection>
  );
}

HomeMediaSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  side: PropTypes.node,
  tracks: PropTypes.array,
  tracksLabel: PropTypes.node,
  tracksMeta: PropTypes.node,
  emptyTracksTitle: PropTypes.node,
  emptyTracksDescription: PropTypes.node,
  uploadAudioLabel: PropTypes.node,
  onUploadTrack: PropTypes.func,
  onPlayTrack: PropTypes.func,
  onSeekTrack: PropTypes.func,
  isEditing: PropTypes.bool,
  onDeleteTrack: PropTypes.func,
  onMoveTrack: PropTypes.func,
  deletingTrackId: PropTypes.string,
  isReorderingTracks: PropTypes.bool,
  deleteTrackLabel: PropTypes.string,
  moveTrackUpLabel: PropTypes.string,
  moveTrackDownLabel: PropTypes.string,
  uploadAudioDisabled: PropTypes.bool,
  uploadAudioTooltip: PropTypes.node,
  uploadAudioTooltipId: PropTypes.string,
  uploadAudioAriaLabel: PropTypes.string,
};
